// Open a sale's popup (the same one Sales / Receivable / Brokers show) from
// any page, by the sale's id: `const sale = useSalePopup(entityName)`, then
// `sale.open(id, { tab: 'payments', bankTxId })` and render `{sale.popup}`.
// Used by Bank (a payment row opens its sale) and Credit Notes (the Sale
// number). The popup is loaded lazily so these pages don't pull in the whole
// Sales page up front.

import { lazy, Suspense, useState, type ReactNode } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { getClientDb } from '../firebase/clientApp';
import RecordSalePaymentModal from './RecordSalePaymentModal';
import { PopupErrorBoundary } from './PopupErrorBoundary';
import type { Entity, SaleInvoice } from '../types';
import type { SalePopupTab } from '../pages/Sales';

const SalePreviewModal = lazy(() => import('../pages/Sales').then(m => ({ default: m.SalePreviewModal })));

export interface OpenSaleOptions {
  tab?: SalePopupTab;
  /** Pick out the payment (or broker payout) behind this bank row. */
  bankTxId?: string;
  /** Pick out this broker on the Brokers tab. */
  brokerId?: string;
}

/** `entityName`: the page's own lookup; left out, company names are loaded on first open. */
export function useSalePopup(entityNameProp?: (id: string) => string): {
  open: (saleId: string, opts?: OpenSaleOptions) => Promise<boolean>;
  popup: ReactNode;
} {
  const [sale, setSale] = useState<SaleInvoice | null>(null);
  const [opts, setOpts] = useState<OpenSaleOptions>({});
  const [payTarget, setPayTarget] = useState<SaleInvoice | null>(null);
  const { business } = useAuth();
  const [names, setNames] = useState<Map<string, string> | null>(null);
  const entityName = entityNameProp ?? ((id: string) => names?.get(id) ?? '—');

  /** Resolves false when there's no such sale (e.g. the id is a purchase). */
  async function open(saleId: string, o: OpenSaleOptions = {}) {
    const db = getClientDb();
    if (!db) return false;
    try {
      const snap = await getDoc(doc(db, 'sales', saleId));
      if (!snap.exists()) return false;
      if (!entityNameProp && !names && business) {
        const es = await getDocs(collection(db, 'entities'));
        setNames(new Map(es.docs.map(d => [d.id, (d.data() as Entity).companyName])));
      }
      setOpts(o);
      setSale({ id: snap.id, ...snap.data() } as SaleInvoice);
      return true;
    } catch { return false; }
  }

  const popup = (
    <PopupErrorBoundary onReset={() => { setPayTarget(null); setSale(null); }}>
      {sale && (
        <Suspense fallback={null}>
          <SalePreviewModal
            invoice={sale}
            entityName={entityName}
            onClose={() => setSale(null)}
            onPay={inv => setPayTarget(inv)}
            initialTab={opts.tab}
            highlightBankTxId={opts.bankTxId}
            highlightBrokerId={opts.brokerId}
          />
        </Suspense>
      )}
      <RecordSalePaymentModal
        invoice={payTarget}
        onClose={() => setPayTarget(null)}
        entityName={payTarget ? entityName(payTarget.toEntityId) : undefined}
      />
    </PopupErrorBoundary>
  );
  return { open, popup };
}
